import { SearchAndSelectTabProps } from "./tabs/SearchAndSelectTabProps";
import { Tab, Tabs } from "@kaltura/ds-react-components";
import { KmsTypeComponent, PreCreationToolProps } from "@kaltura/mediaspace-shared-types";
import { TabPanel, tabProps } from "@kaltura/mediaspace-shared-ui";
import { useState } from "react";
import { Stack, useTheme } from "@kaltura/mediaspace-shared-styled";
import { SearchAndSelectSource } from "./SearchAndSelectSource";
import { TabFactory } from "./tabs/TabFactory";
import { useMediaQuery } from "@kaltura/mediaspace-shared-utils";
import { styled, tabScrollButtonClasses } from "@mui/material";
import { ButtonAnalyticsProvider } from "@kaltura/mediaspace-shared-hooks";
import { CreationToolInjectedProps } from "./creation-tools/CreationToolProps";

export interface SearchAndSelectProps
    extends Omit<SearchAndSelectTabProps, keyof SearchAndSelectSource | "selectedItemsResetter"> {
    className?: string;
    tabs: SearchAndSelectSource[];
    onCreationToolSelected?: (
        component: KmsTypeComponent<any, CreationToolInjectedProps>,
        preComponent?: KmsTypeComponent<any, PreCreationToolProps>
    ) => void;
    /**
     * The aria-label for the search bar input
     */
    searchBarAriaLabel?: string;
}

const StyledTabs = styled(Tabs)({
    [`.${tabScrollButtonClasses.root}`]: {
        width: "auto",
    },
});

/**
 * Search and Select component - A list of tabs with Search and Select widget of different sources (e.g. my media, shared repository, playlists...)
 */
export const SearchAndSelect = ({ className, tabs, searchBarAriaLabel, ...props }: SearchAndSelectProps) => {
    const [activeTab, setActiveTab] = useState(tabs[0].sourceType);
    const [loadedTabs, setLoadedTabs] = useState<any[]>([tabs[0].sourceType]);

    // reset selected items list. we don't care for the value, only
    // for the fact it changes. the MediaTable component listens
    // to these changes in all tabs and resets the selection
    const [selectedItemsResetter, setSelectedItemsResetter] = useState(0);

    const handleChange = (value: any) => {
        setSelectedItemsResetter((v) => v + 1);
        setActiveTab(value);
        setLoadedTabs((loadedTabs) => (loadedTabs.includes(value) ? loadedTabs : [...loadedTabs, value]));
    };

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.sm));

    const hideTabButtons = tabs.length <= 1;

    return (
        <Stack className={className} direction={"column"} gap={{ xs: 2, md: 3 }}>
            {!hideTabButtons && (
                <StyledTabs
                    value={activeTab}
                    onChange={(_event, value) => handleChange(value)}
                    variant={isSmallScreen ? "scrollable" : "standard"}
                    scrollButtons={isSmallScreen}
                    allowScrollButtonsMobile={isSmallScreen}
                >
                    {tabs.map(({ sourceType, name }) => (
                        <Tab key={sourceType} {...tabProps(sourceType)} label={name} />
                    ))}
                </StyledTabs>
            )}
            <div>
                {tabs.map(({ sourceType, name, ...tabProps }) => {
                    const tabContents = (
                        <ButtonAnalyticsProvider key={sourceType} location={`tab - ${sourceType}`}>
                            <TabFactory
                                {...props}
                                {...tabProps}
                                sourceType={sourceType}
                                selectedItemsResetter={selectedItemsResetter}
                                searchBarAriaLabel={searchBarAriaLabel}
                            />
                        </ButtonAnalyticsProvider>
                    );

                    return hideTabButtons ? (
                        tabContents
                    ) : (
                        <TabPanel key={sourceType} currentTab={activeTab} name={sourceType}>
                            {loadedTabs.includes(sourceType) && tabContents}
                        </TabPanel>
                    );
                })}
            </div>
        </Stack>
    );
};
